/**
 * Add Observation Service
 *
 * Service for interacting with the Add Observation NGXS state.
 */

import { Injectable, inject } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
  InitializeAddObservation,
  SetObservationField,
  UpdateObservationFields,
  ResetAddObservation,
  SetObservationData,
  ClearAddObservation,
} from './add-observation.actions';
import { AddObservationState, AddObservationStateModel } from './add-observation.state';
import { Observation } from 'src/app/models/schema';
import { FirestoreService } from 'src/app/services/firestore/firestore';
import { UtilsService } from '../../../services/utils.service';

@Injectable({
  providedIn: 'root',
})
export class AddObservationService {
  /**
   * Observable of the entire add observation state
   */
  @Select(AddObservationState.getState) state$!: Observable<AddObservationStateModel>;

  /**
   * Observable of student ID
   */
  @Select(AddObservationState.getStudentId) studentId$!: Observable<string | undefined>;

  /**
   * Observable of skill ID
   */
  @Select(AddObservationState.getSkillId) skillId$!: Observable<string | undefined>;

  /**
   * Observable of setting
   */
  @Select(AddObservationState.getSetting) setting$!: Observable<string | undefined>;

  /**
   * Observable of observation duration
   */
  @Select(AddObservationState.getObservationDuration) observationDuration$!: Observable<number | undefined>;

  /**
   * Observable of notes
   */
  @Select(AddObservationState.getNotes) notes$!: Observable<string | undefined>;

  /**
   * Observable of observation data
   */
  @Select(AddObservationState.getObservationData) observationData$!: Observable<Partial<Observation>>;

  /**
   * Observable of whether form has data
   */
  @Select(AddObservationState.hasData) hasData$!: Observable<boolean>;

  constructor(
    private store: Store,
    private firestoreService: FirestoreService,
    private utilsService: UtilsService
  ) {}

  /**
   * Get the current state synchronously
   */
  getState(): AddObservationStateModel {
    return this.store.selectSnapshot(AddObservationState.getState);
  }

  /**
   * Get student ID
   */
  getStudentId(): string | undefined {
    return this.store.selectSnapshot(AddObservationState.getStudentId);
  }

  /**
   * Get skill ID
   */
  getSkillId(): string | undefined {
    return this.store.selectSnapshot(AddObservationState.getSkillId);
  }

  /**
   * Get setting
   */
  getSetting(): string | undefined {
    return this.store.selectSnapshot(AddObservationState.getSetting);
  }

  /**
   * Get observation duration
   */
  getObservationDuration(): number | undefined {
    return this.store.selectSnapshot(AddObservationState.getObservationDuration);
  }

  /**
   * Get notes
   */
  getNotes(): string | undefined {
    return this.store.selectSnapshot(AddObservationState.getNotes);
  }

  /**
   * Get observation data
   */
  getObservationData(): Partial<Observation> {
    return this.store.selectSnapshot(AddObservationState.getObservationData);
  }

  /**
   * Check if form has data
   */
  hasData(): boolean {
    return this.store.selectSnapshot(AddObservationState.hasData);
  }

  /**
   * Initialize add observation form
   */
  async initialize(): Promise<void> {
    return this.store.dispatch(new InitializeAddObservation()).toPromise();
  }

  /**
   * Set an observation field
   */
  async setField(field: keyof Observation, value: any): Promise<void> {
    return this.store.dispatch(new SetObservationField(field, value)).toPromise();
  }

  /**
   * Update multiple observation fields
   */
  async updateFields(updates: Partial<Observation>): Promise<void> {
    return this.store.dispatch(new UpdateObservationFields(updates)).toPromise();
  }

  /**
   * Reset add observation form
   */
  async reset(): Promise<void> {
    return this.store.dispatch(new ResetAddObservation()).toPromise();
  }

  /**
   * Set observation data (e.g., when editing)
   */
  async setObservationData(observation: Partial<Observation>): Promise<void> {
    return this.store.dispatch(new SetObservationData(observation)).toPromise();
  }

  /**
   * Clear add observation form
   */
  async clear(): Promise<void> {
    return this.store.dispatch(new ClearAddObservation()).toPromise();
  }

  /**
   * Convert undefined values to null for Firestore compatibility
   */
  private convertUndefinedToNull<T>(obj: T): T {
    if (obj === null || obj === undefined) {
      return null as T;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.convertUndefinedToNull(item)) as T;
    }

    if (typeof obj === 'object') {
      const result: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = (obj as any)[key];
          result[key] = value === undefined ? null : this.convertUndefinedToNull(value);
        }
      }
      return result as T;
    }

    return obj;
  }

  /**
   * Save observation to Firestore
   * @param partnerId - The partner ID
   * @param observation - The observation data to save
   * @returns The generated observation ID
   */
  async saveObservation(partnerId: string, observation: Partial<Observation>): Promise<string> {
    // Generate ID if not present
    const observationId = observation.id || this.utilsService.generateId();
    
    // Add ID to observation
    const observationWithId: Observation = {
      ...observation,
      id: observationId,
    } as Observation;

    // Convert undefined to null for Firestore
    const observationToSave = this.convertUndefinedToNull(observationWithId);

    // Save to Firestore
    const path = `partners/${partnerId}/observations/${observationId}`;
    await this.firestoreService.setDocumentAsync(path, observationToSave);

    return observationId;
  }
}
